import React, { useState, useEffect, useCallback } from "react";
import { FaCheckCircle, FaInfoCircle } from "react-icons/fa";
import privateAxios from "../../utils/axiosPrivate";
import {
  formatDateWithDayjs,
  getFormattedAucklandTime,
  getRelativeTime
} from "../../utils/utils";
import Style from "./profile.module.scss";
import { buttonName, profiletexts } from "../../locales/en";
import PropTypes from "prop-types";
import useProfile from "../../hooks/useProfile";
import {
  getFetchCountryCodeURL,
  userManagementProfileURL
} from "../../utils/constants/apiEndPoints";
import "../../assets/scss/common.scss";
import { NAME_REGEX } from "../../utils/constants/appConstants";

const ProfileField = ({
  label,
  type,
  name,
  value,
  onChange,
  disabled = false,
  placeholder,
  onBlur,
  isValid,
  maxLength,
  showValidationIcon = false
}) => {
  const getMailToLink = (email) => {
    if (!email || typeof email !== "string") return "#";
    return `mailto:${email.trim()}`;
  };
  return (
    <div
      className={`${Style.inputField} ${Style.inputFieldFullwidth} form-group d-flex flex-wrap align-items-center mb-3`}
    >
      <label htmlFor={name} className="col-12 col-md-5 pt-1 pb-1">
        {label || ""}
      </label>
      <span className="col-12 col-md-7 position-relative">
        {disabled && type === "mail" ? (
          <div className={Style.linkField}>
            {value ? (
              <a href={getMailToLink(value)} className="textDecorationNone">
                {value}
              </a>
            ) : (
              profiletexts?.emailText
            )}
          </div>
        ) : (
          <input
            type={type}
            placeholder={placeholder}
            id={name}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            value={value ?? ""}
            disabled={disabled}
            maxLength={maxLength}
          />
        )}
        {showValidationIcon && (
          <span
            className="position-absolute number-verified"
            title={isValid ? profiletexts?.verified : profiletexts?.unverified}
            aria-label={
              isValid ? profiletexts?.verified : profiletexts?.unverified
            }
          >
            {isValid ? (
              <FaCheckCircle className="text-success" />
            ) : (
              <FaInfoCircle className="text-warning" />
            )}
          </span>
        )}
      </span>
    </div>
  );
};

ProfileField.propTypes = {
  label: PropTypes.node.isRequired,
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  onBlur: PropTypes.func,
  isValid: PropTypes.bool,
  maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showValidationIcon: PropTypes.bool
};

const Profile = () => {
  const initialProfileDataValue = {
    firstName: "",
    middleName: "",
    lastName: "",
    displayName: "",
    primaryContactNumber: "",
    secondaryContactNumber: "",
    updatedAt: "",
    isPrimaryContactValidated: false,
    isSecondaryContactValidated: false
  };
  const [profileData, setProfileData] = useState(initialProfileDataValue);

  const [initialProfileData, setInitialProfileData] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [primaryValid, setPrimaryValid] = useState(false);
  const [secondaryValid, setSecondaryValid] = useState(false);
  const { profile } = useProfile();

  const setProfileState = (data) => {
    setProfileData({
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      displayName: data.displayName,
      primaryEmail: data.primaryEmail,
      primaryContactNumber: data.primaryContactNumber,
      secondaryContactNumber: data.secondaryContactNumber,
      updatedAt: data.updatedAt,
      createdAt: data.createdAt,
      isPrimaryContactValidated: data.isPrimaryContactValidated,
      isSecondaryContactValidated: data.isSecondaryContactValidated
    });
    setInitialProfileData({
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      displayName: data.displayName,
      primaryEmail: data.primaryEmail,
      primaryContactNumber: data.primaryContactNumber,
      secondaryContactNumber: data.secondaryContactNumber,
      updatedAt: data.updatedAt,
      isPrimaryContactValidated: data.isPrimaryContactValidated,
      isSecondaryContactValidated: data.isSecondaryContactValidated
    });
  };

  const fetchCountryCode = useCallback(
    async (phoneNo, countryCode) => {
      try {
        const response = await privateAxios.get(getFetchCountryCodeURL, {
          params: { number: phoneNo, countryCode }
        });
        const { data } = response.data;
        if (data.isValid) {
          return data.e164Format;
        } else {
          return null;
        }
      } catch {
        return null;
      }
    },
    [privateAxios]
  );

  const handleBlur = async (event) => {
    const { name, value } = event.target;

    if (name === "secondaryContactNumber" && !value) {
      setProfileData((prevData) => ({
        ...prevData,
        isSecondaryContactValidated: false
      }));
      setSecondaryValid(false);
      return;
    }

    if (name === "primaryContactNumber" && !value) {
      setProfileData((prevData) => ({
        ...prevData,
        isPrimaryContactValidated: false
      }));
      setPrimaryValid(false);
      return;
    }

    if (
      (name === "primaryContactNumber" || name === "secondaryContactNumber") &&
      value
    ) {
      const sanitizedValue = value.startsWith("+") ? value : `+${value}`;

      const code = sanitizedValue.slice(1, 3);

      if (code !== "61" && code !== "64") {
        setProfileData((prevData) => ({
          ...prevData,
          isPrimaryContactValidated:
            name === "primaryContactNumber"
              ? false
              : prevData.isPrimaryContactValidated,
          isSecondaryContactValidated:
            name === "secondaryContactNumber"
              ? false
              : prevData.isSecondaryContactValidated
        }));

        if (name === "primaryContactNumber") {
          setPrimaryValid(false);
        } else if (name === "secondaryContactNumber") {
          setSecondaryValid(false);
        }

        return;
      }

      const phoneNo = sanitizedValue.slice(3);
      const countryCode = code === "61" ? "au" : "nz";

      const e164Format = await fetchCountryCode(phoneNo, countryCode);

      if (e164Format) {
        setProfileData((prevData) => ({
          ...prevData,
          [name]: e164Format,
          isPrimaryContactValidated:
            name === "primaryContactNumber"
              ? true
              : prevData.isPrimaryContactValidated,
          isSecondaryContactValidated:
            name === "secondaryContactNumber"
              ? true
              : prevData.isSecondaryContactValidated
        }));

        if (name === "primaryContactNumber") {
          setPrimaryValid(true);
        } else if (name === "secondaryContactNumber") {
          setSecondaryValid(true);
        }
      } else {
        setProfileData((prevData) => ({
          ...prevData,
          isPrimaryContactValidated:
            name === "primaryContactNumber"
              ? false
              : prevData.isPrimaryContactValidated,
          isSecondaryContactValidated:
            name === "secondaryContactNumber"
              ? false
              : prevData.isSecondaryContactValidated
        }));

        if (name === "primaryContactNumber") {
          setPrimaryValid(false);
        } else if (name === "secondaryContactNumber") {
          setSecondaryValid(false);
        }
      }
    }
  };

  useEffect(() => {
    if (profile && profile?.id) {
      setProfileState(profile);
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let formattedValue = value;

    if (name === "primaryContactNumber" || name === "secondaryContactNumber") {
      // Remove all non-digit characters except the leading +
      if (formattedValue.startsWith("+")) {
        formattedValue = "+" + formattedValue.slice(1).replace(/\D/g, "");
      } else {
        formattedValue = formattedValue.replace(/\D/g, "");
        if (formattedValue.length > 0) {
          formattedValue = "+" + formattedValue;
        }
      }

      // If only "+" is left, clear it
      if (formattedValue === "+") {
        formattedValue = "";
      }
    }

    setProfileData((prevData) => ({
      ...prevData,
      [name]: formattedValue
    }));
  };

  const isProfileDataChanged = (newData, initialData) => {
    return Object.keys(initialData).some(
      (key) => newData[key] !== initialData[key]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields and length
    if (!profileData.firstName) {
      setErrorMessage(profiletexts?.errorMessage?.firstNameRequired);
      setSuccessMessage("");
      return;
    } else if (profileData.firstName.trim().length < 2) {
      setErrorMessage(profiletexts?.errorMessage?.firstNameError);
      setSuccessMessage("");
      return;
    }

    if (!profileData.lastName) {
      setErrorMessage(profiletexts?.errorMessage?.lastNameRequired);
      setSuccessMessage("");
      return;
    } else if (profileData.lastName.trim().length < 2) {
      setErrorMessage(profiletexts?.errorMessage?.lastNameError);
      setSuccessMessage("");
      return;
    }

    if (!profileData.primaryContactNumber) {
      setErrorMessage(profiletexts?.errorMessage?.primaryContactRequired);
      setSuccessMessage("");
      return;
    } else if (profileData.primaryContactNumber.trim().length < 11) {
      setErrorMessage(profiletexts?.errorMessage?.primaryContactError);
      setSuccessMessage("");
      return;
    }

    if (
      profileData.secondaryContactNumber !== "" &&
      profileData.secondaryContactNumber !== null
    ) {
      // Only check if the input is not blank
      if (profileData.secondaryContactNumber.trim().length < 11) {
        setErrorMessage(profiletexts?.errorMessage?.primaryContactError);
        setSuccessMessage("");
        return;
      }
    } else {
      setErrorMessage("");
    }

    // Validate name fields for special characters or numbers
    if (!NAME_REGEX.test(profileData.firstName)) {
      setErrorMessage(profiletexts?.errorMessage?.firstNamePatternError);
      setSuccessMessage("");
      return;
    }

    if (profileData.middleName && !NAME_REGEX.test(profileData.middleName)) {
      setErrorMessage(profiletexts?.errorMessage?.middleNamePatternError);
      setSuccessMessage("");
      return;
    }

    if (!NAME_REGEX.test(profileData.lastName)) {
      setErrorMessage(profiletexts?.errorMessage?.lastNamePatternError);
      setSuccessMessage("");
      return;
    }

    // Check if phone numbers start with 0
    if (
      profileData.primaryContactNumber.startsWith("0") ||
      profileData.primaryContactNumber.startsWith("+0")
    ) {
      setErrorMessage(profiletexts?.errorMessage?.primaryNumberPatternError);
      setSuccessMessage("");
      return;
    }
    if (
      profileData.secondaryContactNumber &&
      (profileData.secondaryContactNumber.startsWith("0") ||
        profileData.secondaryContactNumber.startsWith("+0"))
    ) {
      setErrorMessage(profiletexts?.errorMessage?.secondaryNumberPatternError);
      setSuccessMessage("");
      return;
    }

    function isValidPhoneNumber(number) {
      return /^\+?[1-9]\d*$/.test(number);
    }

    // Primary contact number validation
    if (!isValidPhoneNumber(profileData.primaryContactNumber)) {
      setErrorMessage(profiletexts?.errorMessage?.phoneNumberpatternError);
      setSuccessMessage("");
      return;
    }

    // Secondary contact number validation (if present)
    if (
      profileData.secondaryContactNumber &&
      !isValidPhoneNumber(profileData.secondaryContactNumber)
    ) {
      setErrorMessage(profiletexts?.errorMessage?.phoneNumberpatternError);
      setSuccessMessage("");
      return;
    }

    setErrorMessage("");

    const isChanged =
      initialProfileData &&
      isProfileDataChanged(profileData, initialProfileData);
    if (!isChanged) {
      return;
    }

    try {
      const now = getFormattedAucklandTime();
      const updatedProfileData = {
        displayName: profileData.displayName,
        firstName: profileData.firstName,
        middleName: profileData.middleName,
        lastName: profileData.lastName,
        primaryContactNumber: profileData.primaryContactNumber,
        secondaryContactNumber: profileData.secondaryContactNumber
      };

      await privateAxios.put(userManagementProfileURL, updatedProfileData);

      setInitialProfileData(updatedProfileData);
      setProfileData((prev) => ({ ...prev, updatedAt: now }));

      setSuccessMessage(profiletexts?.errorMessage?.updateSuccessMessage);
      setErrorMessage("");
    } catch {
      setErrorMessage(profiletexts?.errorMessage?.updateErrorMessage);
      setSuccessMessage("");
    }
  };

  return (
    <div className="profile-form">
      {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}
      <form onSubmit={handleSubmit}>
        <ProfileField
          label={profiletexts?.first_name}
          type="text"
          name="firstName"
          value={profileData.firstName}
          onChange={handleChange}
          placeholder={profiletexts?.first_name}
        />
        <ProfileField
          label={profiletexts?.middle_name}
          type="text"
          name="middleName"
          value={profileData.middleName}
          onChange={handleChange}
          placeholder={profiletexts?.middle_name}
        />
        <ProfileField
          label={profiletexts?.last_name}
          type="text"
          name="lastName"
          value={profileData.lastName}
          onChange={handleChange}
          placeholder={profiletexts?.last_name}
        />
        <ProfileField
          label={profiletexts?.customer}
          type="text"
          name="displayName"
          value={profileData.displayName}
          onChange={handleChange}
          disabled
          placeholder={profiletexts?.customer}
        />
        <ProfileField
          label={profiletexts?.email}
          type="mail"
          name="primaryEmail"
          value={profileData.primaryEmail}
          onChange={handleChange}
          disabled
          placeholder={profiletexts?.email}
        />
        <ProfileField
          label={profiletexts?.primary_contact}
          type="tel"
          name="primaryContactNumber"
          value={profileData.primaryContactNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={profiletexts?.primary_contact}
          isValid={primaryValid || profileData.isPrimaryContactValidated}
          showValidationIcon
          maxLength="12"
        />
        <ProfileField
          label={profiletexts?.secondary_contact}
          type="tel"
          name="secondaryContactNumber"
          value={profileData.secondaryContactNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={profiletexts?.secondary_contact}
          isValid={secondaryValid || profileData.isSecondaryContactValidated}
          showValidationIcon
          maxLength="12"
        />
        <ProfileField
          label={profiletexts?.last_update}
          type="text"
          name="updatedAt"
          value={
            profileData?.createdAt
              ? `${formatDateWithDayjs(profileData.updatedAt)} (${getRelativeTime(profileData.updatedAt, "DD-MMM-YYYY hh:mm A")})`
              : ""
          }
          disabled
          placeholder={profiletexts?.last_update}
        />

        <div className="row">
          <div className="col-md-5" />
          <div className={`${Style.inputField} form-group col-12 col-md-7 p-0`}>
            <button
              className={`${Style.updateBtn} btn btn-primary`}
              type="submit"
            >
              {buttonName.update}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Profile;
